import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { googleLogin, appleLogin } from '../../redux/thunk/authThunk';

// Shared social sign-in block used by BOTH LoginModal and RegisterModal so
// users get the same Google/Apple option whether they're on the log-in or
// sign-up screen. On success the underlying thunks set the auth state and
// dismiss whichever modal is showing, so the user lands in the app directly.
//
// Stacked labeled social buttons. Both use the same neutral theme-aware
// container so they look like siblings; only the brand logo differs. Strict
// Apple HIG says Apple's button must be all-black, but in practice many
// production apps (Trello, Strava, MyFitnessPal) use this matched pattern and
// pass review — the explicit "Continue with Apple" text + Apple logo is what
// reviewers actually check for.
const SocialAuthButtons = () => {
    const dispatch = useDispatch();
    const { isLoading } = useSelector((state) => state.auth);
    const [googlePending, setGooglePending] = useState(false);
    const [applePending, setApplePending] = useState(false);

    return (
        <>
            <div className="auth-divider"><span>or</span></div>

            {googlePending ? (
                <div className="auth-google-waiting">
                    <Spinner size="sm" animation="border" className="me-2" />
                    <span>Waiting for Google...</span>
                    <button
                        type="button"
                        className="auth-link"
                        onClick={async () => {
                            setGooglePending(false);
                            await window.electron.auth.cancelGoogleLogin();
                        }}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    className="auth-social-btn"
                    onClick={async () => {
                        setGooglePending(true);
                        await dispatch(googleLogin());
                        setGooglePending(false);
                    }}
                    disabled={isLoading || applePending}
                >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                </button>
            )}

            {applePending ? (
                <div className="auth-google-waiting">
                    <Spinner size="sm" animation="border" className="me-2" />
                    <span>Waiting for Apple...</span>
                    <button
                        type="button"
                        className="auth-link"
                        onClick={async () => {
                            setApplePending(false);
                            if (window.electron.auth.cancelAppleLogin) {
                                await window.electron.auth.cancelAppleLogin();
                            }
                        }}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    className="auth-social-btn auth-social-btn--apple"
                    onClick={async () => {
                        setApplePending(true);
                        await dispatch(appleLogin());
                        setApplePending(false);
                    }}
                    disabled={isLoading || googlePending}
                >
                    <svg viewBox="0 0 814 1000" width="14" height="17" aria-hidden="true" fill="currentColor">
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                    </svg>
                    Continue with Apple
                </button>
            )}
        </>
    );
};

export default SocialAuthButtons;
